/**
 * @ Author: Housefolios
 * @ Create Time: 2021-11-22 08:46:15
 * @ Modified by: David Helmick
 * @ Modified time: 2025-05-08 16:58:29
 * @ Description: Dialog to send a property or selected properties to the trash
 */

import React, { useState } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  IconButton,
  ListItem,
  Tooltip,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  Box,
} from '@mui/material'

// Centralized lodash imports - much cleaner!
import { map, concat } from '@/utils/lodash'

import { enqueueSnackbar } from 'notistack'
import {
  useMutation,
  useLazyQuery,
  useQuery,
} from '@apollo/client/react'
import { GET_ORGS_PROPERTIES_SHARED_WITH } from '@/housefolios-components/Property/queries'
import { GET_PROPERTIES, GET_TRASH } from '../queries'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'
import { UPDATE_PROPERTIES } from '@/housefolios-components/Portfolio/mutations'
import withSession from '@/housefolios-components/Session/withSession'
import { GET_PROPERTIES_QUERY_LIMIT } from '@/utils/pagination'
import { GET_PROPERTY } from '@/housefolios-components/PropertyContainer/queries'

function TrashPropertiesComponent(props) {
  const { bulkActionsNotifications, rightClickMenu } = props
  const [loader, setLoader] = useState(false)
  const [openDeleteProperty, setDeleteProperty] = useState(false)
  const handleOpenDeleteProperty = () => {
    setDeleteProperty(true)
  }
  const handleCloseDeleteProperty = () => {
    setDeleteProperty(false)
  }
  const [confirm, setConfirm] = useState(false)

  const [updateProperties] = useMutation(UPDATE_PROPERTIES)

  const handleTrash = () => {
    setConfirm(!confirm)
    setDeleteProperty(false)
    if (props.gotToPortfolio)
      localStorage.setItem('portfolio', props.portfolioId)
    setLoader(true)
    updateProperties({
      variables: {
        ids: props.selected,
        propertyInput: {
          deleted: true,
          deletedAt: new Date(),
          deletedOrg: props.session.me.activeOrg._id,
        },
      },
      refetchQueries: concat(
        [
          {
            query: GET_PROPERTIES,
            variables: {
              portfolioId: [props.portfolioId],
              limit: GET_PROPERTIES_QUERY_LIMIT,
            },
          },
          {
            query: GET_PROPERTIES,
            variables: {
              portfolioId: map(
                props.portfolios,
                (portfolio) => portfolio._id,
              ),
              limit: GET_PROPERTIES_QUERY_LIMIT,
            },
          },
          {
            query: GET_TRASH,
          },
          {
            query: GET_PORTFOLIOS,
          },
          {
            query: GET_ME,
          },
        ],
        map(props.selected, (id) => {
          return {
            query: GET_PROPERTY,
            variables: { id },
          }
        }),
      ),
      // onCompleted: (data) =>
      //   console.log(
      //     'Trash property',
      //     data.updateProperties.__typename,
      //     data,
      //   ),
    })
      .then(() => {
        enqueueSnackbar(`Selected Properties Trashed`, {
          variant: 'success',
          autoHideDuration: 3000,
        })
        if (props.setSelected) props.setSelected([])
      })
      .catch((error) => {
        enqueueSnackbar(`Failed to Trash Selected Properties`, {
          variant: 'error',
          autoHideDuration: 3000,
        })
      })
      .finally(() => {
        setLoader(false)
      })
  }

  const { loading, error, data } = useQuery(
    GET_ORGS_PROPERTIES_SHARED_WITH,
    {
      variables: {
        propertyIds: props.selected,
      },
    },
  )

  if (loading) return 'loading'
  if (error) return `Error! ${error.message}`
  const { orgsPropertiesSharedWith } = data
  if (orgsPropertiesSharedWith.length > 0) {
    return (
      <>
        {bulkActionsNotifications && (
          <Tooltip title="Bulk Trash">
            <IconButton
              onClick={handleOpenDeleteProperty}
              disabled={loader}
              size="large"
            >
              <FontAwesomeIcon
                icon={['far', 'trash-can']}
                style={{ color: 'white' }}
              />
            </IconButton>
          </Tooltip>
        )}
        <Dialog
          open={openDeleteProperty}
          onClose={handleCloseDeleteProperty}
        >
          <Box sx={{ textAlign: 'center', p: '3rem' }}>
            <Box sx={[{ borderRadius: '50%', m: 0 }, {
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mr: "0.25rem",
              position: "relative",
              fontSize: "1.2rem",
              transition: "all 0.2s ease-in-out",
              "&:last-child": { mr: 0, marginRight: 0 },
              "& .badge": { position: "absolute", right: 0, bottom: 0 },
              "& svg, & .svg-inline--fa": { display: "block" },
              marginRight: "0.25rem",
            }]}>
              <Box sx={[{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                border: 0,
                backgroundColor: "rgb(100%, 95.9215686275%, 96.418938307%) !important",
                height: "130px !important",
                width: "130px !important",
                lineHeight: "130px !important",
                p: 0,
                borderRadius: "50%",
                color: "#f83245",
                m: 0,
              }, {
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                mr: "0.25rem",
                position: "relative",
                fontSize: "1.2rem",
                transition: "all 0.2s ease-in-out",
                "&:last-child": { mr: 0, marginRight: 0 },
                "& .badge": { position: "absolute", right: 0, bottom: 0 },
                "& svg, & .svg-inline--fa": { display: "block" },
                marginRight: "0.25rem",
              }]}>
                <FontAwesomeIcon icon={['far', 'trash-can']} size="10x" style={{ display: 'flex', alignSelf: 'center' }} sx={{ fontSize: "2.5rem", fontWeight: "300", lineHeight: "1.2" }} />
              </Box>
            </Box>
            <Box
              component="h4"
              sx={{ fontWeight: 700, mt: '1.5rem' }}
            >
              Trash Shared Properties?
            </Box>
            <Box sx={[{ mb: 0, color: '#a6a6b9' }, { fontSize: "1.15rem !important" }]}>
              <FormControl component="fieldset" sx={{ pr: '1.5rem' }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirm}
                        onChange={() => setConfirm(!confirm)}
                      />
                    }
                    label="Remove one or more shared properites for all users?"
                  />
                </FormGroup>
              </FormControl>
              {/* Delete this portfolio along with all the properties inside. This can't be undone. */}
            </Box>
            <Box sx={{ pt: '1.5rem' }}>
              <Button
                variant="text"
                onClick={handleCloseDeleteProperty}
                //
              >
                <Box component="span" sx={{
                  display: "inline-block",
                  "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                  "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                }}>Cancel</Box>
              </Button>
              <Button
                variant="contained"
                style={{
                  backgroundColor: '#f83145',
                  color: 'white',
                }}
                href={props.goToTrash ? '/trash' : null}
                onClick={handleTrash}
                disabled={!confirm}
              >
                <Box component="span" sx={{
                  display: "inline-block",
                  "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
                  "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
                }}>Trash</Box>
              </Button>
            </Box>
          </Box>
        </Dialog>
      </>
    )
  } else {
    return (
      <>
        {bulkActionsNotifications && (
          <Tooltip title="Bulk Trash">
            <IconButton
              onClick={handleTrash}
              disabled={loader}
              size="large"
            >
              <FontAwesomeIcon
                icon={['far', 'trash-can']}
                style={{ color: 'white' }}
              />
            </IconButton>
          </Tooltip>
        )}
      </>
    )
  }
}

export default withSession(TrashPropertiesComponent)
